import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import foo_logo from "../../assets/foo_mascot_chef_hat@2x.svg";
import { PARTNER_LOGO_UPLOAD, PARTNER_COVER_UPLOAD, GET_PARTNER } from "./queries";
import { has_access } from "../../../middleware/privileges";
import { lang } from "../../../middleware/lang";

const getImageUrl = (path: string | null, fallback?: string): string => {
  if (!path) return fallback || "";
  if (path.startsWith("http")) return path;
  return `https://cdn.foopos.mx${path.replace("/img", "").slice(0)}`;
};

const General: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("user") || "");
  const canEditInfo = has_access(128); // edit_info
  const [logoUrl, setLogoUrl] = useState<string>(
    getImageUrl(user?.partner?.logo, foo_logo)
  );
  const [coverUrl, setCoverUrl] = useState<string>(
    getImageUrl(user?.partner?.cover_img)
  );
  // Logo upload
  const [uploadLogo, { loading }] = useMutation(PARTNER_LOGO_UPLOAD, {
    refetchQueries: [{ query: GET_PARTNER }],
    onCompleted: (data) => {
      const newLogo = data.partnerLogoUpload.logo;
      setLogoUrl(getImageUrl(newLogo, foo_logo));
      const updatedUser = { ...user, partner: { ...user.partner, logo: newLogo } };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        uploadLogo({ variables: { file: acceptedFiles[0] } });
      }
    },
    [uploadLogo]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    accept: "image/png,image/jpeg",
    disabled: loading || !canEditInfo,
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors[0];
      if (error?.code === "file-too-large") {
        alert("El archivo es demasiado grande. El tamaño máximo es 5MB.");
      } else if (error?.code === "file-invalid-type") {
        alert("Solo se permiten archivos PNG y JPG.");
      }
    },
  });

  // Cover upload
  const [uploadCover, { loading: coverLoading }] = useMutation(PARTNER_COVER_UPLOAD, {
    refetchQueries: [{ query: GET_PARTNER }],
    onCompleted: (data) => {
      const newCover = data.partnerCoverUpload.cover_img;
      setCoverUrl(getImageUrl(newCover));
      const updatedUser = { ...user, partner: { ...user.partner, cover_img: newCover } };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    },
  });

  const onDropCover = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        uploadCover({ variables: { file: acceptedFiles[0] } });
      }
    },
    [uploadCover]
  );

  const {
    getRootProps: getCoverRootProps,
    getInputProps: getCoverInputProps,
  } = useDropzone({
    onDrop: onDropCover,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    accept: "image/png,image/jpeg",
    disabled: coverLoading || !canEditInfo,
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors[0];
      if (error?.code === "file-too-large") {
        alert("El archivo es demasiado grande. El tamaño máximo es 5MB.");
      } else if (error?.code === "file-invalid-type") {
        alert("Solo se permiten archivos PNG y JPG.");
      }
    },
  });

  return (
    <div className="row justify-content-center mt-3">
      <div className="col">
        <div className="card sticky-top">
          <div style={{ height: 100, backgroundColor: "#3e72af" }} />
          <div className="card-body text-center">
            {canEditInfo ? (
              <div {...getRootProps()} style={{ cursor: "pointer" }}>
                <input {...getInputProps()} />
                <button type="button" className="btn btn-transparent user_avatar">
                  {loading ? (
                    <div style={{ padding: "20px" }}>
                      <i className="fas fa-spinner fa-spin fa-2x"></i>
                      <p className="mt-2 mb-0 text-muted">Subiendo...</p>
                    </div>
                  ) : (
                    <img
                      src={logoUrl}
                      className=""
                      style={{ maxWidth: "60%" }}
                      alt="companylogo"
                    />
                  )}
                </button>
                <small className="text-muted d-block mb-2">
                  Click para cambiar logo
                </small>
              </div>
            ) : (
              <div>
                <button type="button" className="btn btn-transparent user_avatar">
                  <img
                    src={logoUrl}
                    className=""
                    style={{ maxWidth: "60%" }}
                    alt="companylogo"
                  />
                </button>
              </div>
            )}
            <p>
              {user.partner.company_name}
              <br />
              {user.email} <br />
              {user.name} <br />
              <br />
              {`v.${process.env.REACT_APP_CURRENT_GIT_SHA}`}
            </p>
            <button
              type="button"
              className="btn btn-link text-danger btn-sm mt-4"
            >
              Cambiar Password
            </button>
          </div>
        </div>

        {/* Cover / Background image */}
        <div
          className="card mt-4"
          style={{ overflow: "hidden" }}
        >
          <div className="card-body">
            <h6 style={{ fontWeight: 700, marginBottom: 4 }}>
              {lang("Imagen de portada", "Cover image")}
            </h6>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              {lang(
                "Esta imagen se muestra como fondo de tu restaurante en la app. Recomendamos una imagen horizontal de al menos 1200x400px.",
                "This image is shown as the background of your restaurant in the app. We recommend a horizontal image of at least 1200x400px."
              )}
            </p>

            {canEditInfo ? (
              <div
                {...getCoverRootProps()}
                style={{
                  cursor: "pointer",
                  border: "2px dashed #ddd",
                  borderRadius: 12,
                  overflow: "hidden",
                  position: "relative",
                  background: "#f8f9fa",
                }}
              >
                <input {...getCoverInputProps()} />
                {coverLoading ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 180,
                    }}
                  >
                    <i className="fas fa-spinner fa-spin fa-2x text-muted"></i>
                    <p className="mt-2 mb-0 text-muted">
                      {lang("Subiendo...", "Uploading...")}
                    </p>
                  </div>
                ) : coverUrl ? (
                  <div style={{ position: "relative" }}>
                    <img
                      src={coverUrl}
                      alt="cover"
                      style={{
                        width: "100%",
                        height: 180,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <i className="fas fa-camera mr-1" />
                      {lang("Cambiar", "Change")}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 180,
                      color: "#999",
                    }}
                  >
                    <i className="fas fa-image fa-3x mb-2" />
                    <span style={{ fontWeight: 600 }}>
                      {lang(
                        "Click o arrastra una imagen",
                        "Click or drag an image"
                      )}
                    </span>
                    <small>PNG, JPG — max 5MB</small>
                  </div>
                )}
              </div>
            ) : (
              coverUrl ? (
                <div style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={coverUrl}
                    alt="cover"
                    style={{
                      width: "100%",
                      height: 180,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: 180,
                    borderRadius: 12,
                    background: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ccc",
                  }}
                >
                  <i className="fas fa-image fa-3x" />
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <Link className="btn btn-block btn-danger mt-4" to="/logout">
        Cerrar sesión
      </Link>
      <button
        className="btn btn-block btn-outline-secondary mt-4"
        onClick={() => {
          window.location.reload();
        }}
      >
        Actuzalizar Versión
      </button>
    </div>
  );
};

export default General;
